// Global axios configuration for secure cookie-based auth + CSRF.
// Imported once for its side effects (sets defaults + interceptors on the shared
// axios singleton used across the app).
import axios from "axios";
import { recordNetworkError } from "./diagnostics";

axios.defaults.withCredentials = true;

export function getCookie(name) {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

let csrfPromise = null;
let cachedCsrf = null;

const PREAUTH_MUTATION_PATHS = [
  "/api/nationals/login",
  "/api/subscribers/login",
  "/api/subscribers/register",
  "/api/auth/register",
  "/api/yasharal-live/auth/login",
  "/api/yasharal-live/auth/signup",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
];

function isPreAuthMutation(config) {
  const method = (config.method || "get").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(method)) return false;
  const rawUrl = String(config.url || "");
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    return PREAUTH_MUTATION_PATHS.some((path) => parsed.pathname === path);
  } catch {
    return PREAUTH_MUTATION_PATHS.some((path) => rawUrl.endsWith(path));
  }
}

async function getCsrfToken() {
  const localCookie = getCookie("ysq_csrf");
  if (localCookie) {
    cachedCsrf = localCookie;
    return localCookie;
  }

  if (cachedCsrf) return cachedCsrf;
  if (csrfPromise) return csrfPromise;

  const backend = process.env.REACT_APP_BACKEND_URL;
  csrfPromise = axios
    .get(`${backend}/api/session/csrf`, {
      withCredentials: true,
      headers: { "X-YSQ-CSRF-Bridge": "1" },
    })
    .then((response) => {
      cachedCsrf = response?.data?.csrf_token || null;
      return cachedCsrf;
    })
    .catch((error) => {
      if (error?.response?.status === 401) {
        cachedCsrf = null;
        return null;
      }
      throw error;
    })
    .finally(() => {
      csrfPromise = null;
    });

  return csrfPromise;
}

function routeProtectedNationRequest(config) {
  const url = config.url || "";
  const method = (config.method || "get").toLowerCase();

  if (url.endsWith("/api/nationals") && ["get", "post"].includes(method)) {
    config.url = url.replace(
      /\/api\/nationals$/,
      "/api/secure-records/national-applications"
    );
    return config;
  }

  if (method === "get" && url.endsWith("/api/admin/nationals/csv")) {
    config.url = url.replace(
      /\/api\/admin\/nationals\/csv$/,
      "/api/secure-records/national-applications/csv"
    );
    return config;
  }

  const applicationMatch = url.match(
    /\/api\/admin\/national-applications\/([^/?]+)(?:\/(review|approve|deny))?$/
  );
  if (applicationMatch) {
    const applicationId = applicationMatch[1];
    const action = applicationMatch[2];
    config.url = `${process.env.REACT_APP_BACKEND_URL}/api/secure-records/national-applications/${applicationId}${action ? `/${action}` : ""}`;
    return config;
  }

  if (method === "get" && url.match(/\/api\/nationals\/admin\/users(?:\?.*)?$/)) {
    config.url = url.replace(
      /\/api\/nationals\/admin\/users/,
      "/api/secure-records/users"
    );
    return config;
  }

  if (method === "get") {
    const userMatch = url.match(/\/api\/nationals\/admin\/users\/([^/?]+)$/);
    if (userMatch) {
      config.url = `${process.env.REACT_APP_BACKEND_URL}/api/secure-records/users/${userMatch[1]}`;
      return config;
    }
  }

  // The legacy server.py admin-user PATCH handler has state/indentation bugs.
  // Route only edit-user saves to the isolated stable v2 handler.
  if (method === "patch") {
    const updateMatch = url.match(/\/api\/nationals\/admin\/users\/([^/?]+)$/);
    if (updateMatch) {
      config.url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/users-v2/${updateMatch[1]}`;
      return config;
    }
  }

  return config;
}

axios.interceptors.request.use(async (config) => {
  routeProtectedNationRequest(config);
  config.__diagStartedAt = typeof performance !== "undefined" ? performance.now() : Date.now();

  if (isPreAuthMutation(config)) {
    return config;
  }

  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrf = await getCsrfToken();
    if (csrf) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (!status || status >= 500) {
      const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
      const startedAt = Number(error?.config?.__diagStartedAt);
      recordNetworkError({
        method: error?.config?.method,
        url: error?.config?.url,
        status,
        duration_ms: Number.isFinite(startedAt) ? endedAt - startedAt : null,
        request_id: error?.response?.headers?.["x-request-id"] || error?.response?.headers?.["x-correlation-id"] || null,
      });
    }

    if (
      error?.response?.status === 403 &&
      String(error?.response?.data?.detail || "").toLowerCase().includes("csrf")
    ) {
      cachedCsrf = null;
    }
    return Promise.reject(error);
  }
);
